import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Ticket, CalendarDays, GraduationCap, Clock, Lock, Send } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ShareBooking from "@/components/ShareBooking";
import { paymentLabel, proofWhatsAppUrl, waNumber } from "@/components/PaymentMethodPicker";
import { format, parseISO } from "date-fns";
import { enGB, fr as frLocale } from "date-fns/locale";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";

const curLang = () => localStorage.getItem("padel-lang") || "en";
const tr = (key) => translations[curLang()]?.[key] ?? translations.en[key] ?? key;
export const niceDate = (d) => format(parseISO(d), "do MMMM yyyy", { locale: curLang() === "en" ? enGB : frLocale });

export const StatusBadge = ({ status }) => (
  <Badge className={`rounded-full text-xs ${status === "confirmed" ? "bg-green-500/10 text-green-500" : status === "cancelled" ? "bg-red-500/10 text-red-500" : "bg-golden/10 text-golden"}`} data-testid="status-badge">
    {tr(`mb.status_${status}`)}
  </Badge>
);

export const PaymentBadge = ({ item }) => (
  <Badge variant="outline" className={`rounded-full text-[10px] ${item.payment_status === "paid" ? "border-green-500/30 text-green-500" : "border-border text-muted-foreground"}`}>
    {item.payment_method === "cash" ? (item.payment_status === "paid" ? tr("mb.paid_court") : tr("mb.pay_court")) : `${paymentLabel(item.payment_method)} · ${tr(item.payment_status === "paid" ? "mb.paid" : "mb.unpaid")}`}
  </Badge>
);

export const RefundBadge = ({ item }) => {
  if (!item.refund_status) return null;
  const done = item.refund_status === "refunded";
  return (
    <Badge variant="outline" className={`rounded-full text-[10px] ${done ? "border-green-500/30 text-green-500" : "border-golden/40 text-golden"}`} data-testid={`refund-badge-${item.refund_status}`}>
      {done ? tr("mb.refunded") : tr("mb.refund_pending")}
    </Badge>
  );
};


export const canCancel = (item) => new Date(`${item.date}T${item.start_time}:00+04:00`) - new Date() >= 24 * 60 * 60 * 1000;

export const CancelButton = ({ item, onCancel, testId, club }) => {
  const { t } = useLanguage();
  if (canCancel(item)) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full text-xs text-destructive border-destructive/40 hover:bg-destructive/10" data-testid={testId}>{t("mb.cancel")}</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="sm:max-w-sm" data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("mb.cancel_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {niceDate(item.date)} · {item.start_time}–{item.end_time}{item.coach_name ? ` ${t("mb.with")} ${item.coach_name}` : ""}. {t("mb.cancel_desc")}
              {item.payment_method === "juice" && item.payment_status === "paid" && (
                <span className="block mt-2 text-foreground" data-testid="cancel-refund-note">{t("mb.cancel_refund")}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="cancel-confirm-no">{t("mb.keep")}</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel} className="rounded-full bg-destructive hover:bg-destructive/90 text-white" data-testid="cancel-confirm-yes">{t("mb.yes_cancel")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full text-xs text-muted-foreground border-border gap-1" data-testid={testId} aria-label={t("mb.locked_title")}>
          <Lock size={12} /> {t("mb.cancel")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-sm" data-testid="cancel-policy-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("mb.locked_title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {niceDate(item.date)} · {item.start_time}–{item.end_time} {t("mb.locked_desc")}
            <span className="block mt-2" data-testid="cancel-policy-text">{t("mb.policy")}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="cancel-policy-close">{t("mb.got_it")}</AlertDialogCancel>
          <AlertDialogAction asChild>
            <a href={`https://wa.me/${waNumber(club?.whatsapp) || "23057299188"}?text=${encodeURIComponent(t("mb.wa_help", { ref: item.reference || "", date: niceDate(item.date), time: item.start_time }))}`} target="_blank" rel="noreferrer" className="rounded-full bg-coral hover:bg-coral/90 text-white gap-1" data-testid="cancel-policy-whatsapp">
              <Send size={14} /> {t("mb.wa_club")}
            </a>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const SendProofButton = ({ club, item, kind }) => (
  item.payment_method === "juice" && item.payment_status !== "paid" && item.status === "pending" ? (
    <a href={proofWhatsAppUrl(club, item, kind)} target="_blank" rel="noreferrer">
      <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white gap-1 h-7 text-xs" data-testid="send-proof-btn"><Send size={11} /> {tr("mb.send_proof")}</Button>
    </a>
  ) : null
);

export default function MyBookings() {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([api.get("/bookings"), api.get("/coaching/sessions"), api.get("/club")])
      .then(([b, s, c]) => { setBookings(b.data); setSessions(s.data); setClub(c.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (url) => {
    try { await api.put(url); toast.success(t("mb.cancelled_toast")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;

  const today = new Date().toISOString().slice(0, 10);
  const sortUpcoming = (arr) => [...arr].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  const upcoming = sortUpcoming(bookings.filter(b => b.date >= today && b.status !== "cancelled"));
  const past = bookings.filter(b => b.date < today || b.status === "cancelled");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="my-bookings-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-coral/10 flex items-center justify-center"><Ticket size={24} className="text-coral" /></div>
            <div>
              <h1 className="font-heading font-black text-3xl tracking-tight">{t("mb.title")}</h1>
              <p className="text-muted-foreground text-sm">{t("mb.subtitle")}</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="court">
          <TabsList className="rounded-full bg-muted/50 p-1 mb-6">
            <TabsTrigger value="court" className="rounded-full text-sm gap-1" data-testid="tab-court-bookings"><CalendarDays size={14} /> {t("mb.tab_court")} ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="coaching" className="rounded-full text-sm gap-1" data-testid="tab-coaching-bookings"><GraduationCap size={14} /> {t("mb.tab_coaching")} ({sessions.filter(s => s.status !== "cancelled").length})</TabsTrigger>
          </TabsList>

          <TabsContent value="court" className="space-y-3">
            {upcoming.length === 0 && <EmptyState text={t("mb.no_upcoming")} linkText={t("mb.book")} />}
            {upcoming.map(b => <BookingRow key={b._id} b={b} club={club} onCancel={() => cancel(`/bookings/${b._id}/cancel`)} />)}
            {past.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-wider text-muted-foreground pt-6 pb-1">{t("mb.past")}</p>
                {past.map(b => <BookingRow key={b._id} b={b} muted />)}
              </>
            )}
          </TabsContent>

          <TabsContent value="coaching" className="space-y-3">
            {sessions.length === 0 && <EmptyState text={t("mb.no_lessons")} link="/coaching" linkText={t("nav.coaching")} />}
            {sortUpcoming(sessions).map(s => (
              <Card key={s._id} className={`border-border/50 ${s.status === "cancelled" ? "opacity-60" : ""}`} data-testid="coaching-session-row">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm flex items-center gap-2"><GraduationCap size={14} className="text-teal" /> {t("confirm.lesson_with", { coach: s.coach_name })}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Clock size={11} /> {niceDate(s.date)} · {s.start_time}–{s.end_time} · Rs {s.price} · {t("mb.ref")} <span className="font-mono">{s.reference}</span></p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <PaymentBadge item={s} /><StatusBadge status={s.status} />
                    <SendProofButton club={club} item={s} kind="coaching" />
                    {s.status !== "cancelled" && s.payment_status !== "paid" && <CancelButton item={s} club={club} onCancel={() => cancel(`/coaching/sessions/${s._id}/cancel`)} testId="cancel-session-btn" />}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}

export function BookingRow({ b, club, onCancel, muted }) {
  return (
    <Card className={`border-border/50 ${muted ? "opacity-60" : ""}`} data-testid="booking-row">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-sm">{niceDate(b.date)} · <span className="text-golden">{b.start_time}–{b.end_time}</span></p>
          <p className="text-xs text-muted-foreground mt-1">
            Rs {Number(b.total_price).toLocaleString()} · {tr("mb.ref")} <span className="font-mono">{b.reference || "—"}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PaymentBadge item={b} /><StatusBadge status={b.status} /><RefundBadge item={b} />
          {!muted && <SendProofButton club={club} item={b} kind="court" />}
          {!muted && <ShareBooking booking={b} clubName="D&D Padel" />}
          {!muted && <CancelButton item={b} club={club} onCancel={onCancel} testId="cancel-booking-btn" />}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ text, link = "/book", linkText }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <p className="mb-2">{text}</p>
      <Link to={link} className="text-coral underline text-sm">{linkText}</Link>
    </div>
  );
}
